import React, { useState, useEffect } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ModuleDetailsEditorProps {
  module: {
    id: string;
    module_number: number;
    part_number: number;
    title: string;
    duration_minutes: number;
  };
  courseId: string;
  onDelete: () => void;
  onChange: (moduleId: string, data: any) => void;
}

export function ModuleDetailsEditor({ module, courseId, onDelete, onChange }: ModuleDetailsEditorProps) {
  const [formData, setFormData] = useState(module);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Update form data when module prop changes
  useEffect(() => {
    setFormData(module);
  }, [module]);

  // Call onChange whenever formData changes
  const handleInputChange = (field: string, value: string | number) => {
    const newData = {
      ...formData,
      [field]: value
    };
    setFormData(newData);
    onChange(module.id, newData);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Input
            type="number"
            value={formData.module_number}
            onChange={(e) => handleInputChange('module_number', parseInt(e.target.value) || 0)}
            className="w-20"
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            value={formData.part_number}
            onChange={(e) => handleInputChange('part_number', parseInt(e.target.value) || 0)}
            className="w-20"
          />
        </TableCell>
        <TableCell>
          <Input
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            value={formData.duration_minutes}
            onChange={(e) => handleInputChange('duration_minutes', parseInt(e.target.value) || 0)}
            className="w-24"
          />
        </TableCell>
        <TableCell>
          <Button 
            variant="destructive" 
            onClick={() => setIsDeleteDialogOpen(true)}
            size="sm"
          >
            Delete
          </Button>
        </TableCell>
      </TableRow>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the module
              and remove it from the course.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setIsDeleteDialogOpen(false);
              onDelete();
            }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
